// Import
import React, { useEffect, useState } from "react";
import {
  Typography,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
} from "@mui/material";
import apiClient from "../../utils/apiClient";
import escapeHtml from "lodash/escape";
import CustomAutocompleteField from "../custom/CustomAutoCompleteField";
import CustomMultiAutocompleteField from "../custom/CustomMultiAutoCompleteField";
import CustomButton from "../custom/CustomButton";
import Swal from "../../utils/swal";
import { showSuccessToast, showErrorToast } from "../../utils/swalToast";
import RowActionsMenu from "../custom/RowActionsMenu";
import CustomDataGrid from "../custom/CustomDataGrid";

export default function AdminDaerahMenu() {
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refresh, setRefresh] = useState(false);

  const [openAddModal, setOpenAddModal] = useState(false);

  const [listAnggota, setListAnggota] = useState("");
  const [listAnggotaItems, setListAnggotaItems] = useState([]);
  const [listDaerah, setListDaerah] = useState([]);
  const [listDaerahItems, setListDaerahItems] = useState([]);

  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedAdminUuid, setSelectedAdminUuid] = useState(null);

  useEffect(() => {
    apiClient
      .get("/admin-daerah")
      .then((response) => {
        setRows(
          response.data.data.map((admin, index) => ({
            id: index + 1 + (page - 1) * 10,
            uuid: admin.uuid,
            name: admin.name,
            daerah: admin.daerah,
          }))
        );
      })
      .catch((error) => {
        console.error("Error fetching admins", error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [refresh, page]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await apiClient.get("/users/non-admin-daerah");
        setListAnggotaItems(
          response.data.data.map((item) => ({
            userId: item.uuid,
            namaAnggota: item.nama_lengkap,
            emailAnggota: item.email,
          }))
        );
      } catch (error) {
        alert("Error fetching anggota", error);
      }
    };
    fetchData();
  }, [refresh]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await apiClient.get("/provinces");
        setListDaerahItems(
          response.data.data.map((item) => ({
            id: item.id,
            name: item.name,
          }))
        );
      } catch (error) {
        alert("Error fetching daerah", error);
      }
    };
    fetchData();
  }, []);

  const handleUpsertAdminDaerah = async () => {
    if (!isEditMode && !listAnggota) {
      return showErrorToast("Harap isi nama anggota");
    }

    if (!listDaerah || listDaerah.length === 0) {
      return showErrorToast("Pilih setidaknya satu daerah");
    }

    const result = await Swal.fire({
      title: "Konfirmasi",
      html: `Apakah anda yakin ingin ${
        isEditMode ? "mengubah" : "menambahkan"
      } <span style="color:#A92027;">${
        escapeHtml(listAnggota?.namaAnggota) || "-"
      }</span> pada daerah berikut: <br>${listDaerah
        .map((daerah) => `<span style="color:#A92027;">${daerah.name}</span>`)
        .join(", ")}`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Ya",
      cancelButtonText: "Batal",
      confirmButtonColor: "#003158",
      cancelButtonColor: "#A92027",
      didOpen: () => {
        document.querySelector(".swal2-container").style.zIndex = "9999";
        document.querySelector(".swal2-popup").style.zIndex = "99999";
      },
    });

    if (!result.isConfirmed) return;

    try {
      if (isEditMode) {
        await apiClient.put("/admin-daerah", {
          idAdmin: selectedAdminUuid,
          listDaerah,
        });
        showSuccessToast("Admin berhasil diubah");
      } else {
        await apiClient.post("/admin-daerah", {
          idAdmin: listAnggota.userId,
          listDaerah,
        });
        showSuccessToast("Admin berhasil ditambahkan");
      }

      setOpenAddModal(false);
      setListAnggota("");
      setListDaerah([]);
      setRefresh((prev) => !prev);
    } catch (error) {
      showErrorToast(error?.response?.data?.msg || "Terjadi kesalahan");
    }
  };
  const handleDeleteAdminDaerah = async (adminUuid, adminName) => {
    const result = await Swal.fire({
      title: "Konfirmasi",
      html: `Apakah anda yakin ingin mencopot status admin daerah pada anggota <span style="color:#A92027;">${escapeHtml(adminName)}</span> ?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Ya",
      cancelButtonText: "Batal",
      confirmButtonColor: "#003158",
      cancelButtonColor: "#A92027",
      didOpen: () => {
        document.querySelector(".swal2-container").style.zIndex = "9999";
        document.querySelector(".swal2-popup").style.zIndex = "99999";
      },
    });

    if (!result.isConfirmed) return;

    try {
      const response = await apiClient.post("/admin-daerah/delete", {
        idAdmin: adminUuid,
      });
      showSuccessToast(response.data.msg || "Admin berhasil dihapus.");
      setRefresh((prev) => !prev);
    } catch (error) {
      showErrorToast(error?.response?.data?.msg || "Terjadi kesalahan");
    }
  };

  const handleOpenAddModal = () => {
    setIsEditMode(false);
    setListAnggota("");
    setListDaerah([]);
    setOpenAddModal(true);
  };

  const handleEditAdminDaerah = (adminUuid) => {
    const selectedAdmin = rows.find((row) => row.uuid === adminUuid);
    if (selectedAdmin) {
      setIsEditMode(true);
      setSelectedAdminUuid(adminUuid);

      const anggota = listAnggotaItems.find(
        (item) => item.userId === adminUuid
      ) || {
        userId: selectedAdmin.uuid,
        namaAnggota: selectedAdmin.name,
        emailAnggota: "-",
      };

      setListAnggota(anggota);

      const daerahObjects = selectedAdmin.daerah.map((name) =>
        listDaerahItems.find((item) => item.name === name)
      );
      setListDaerah(daerahObjects.filter(Boolean));
      setOpenAddModal(true);
    }
  };

  const handleCloseAddModal = () => {
    setOpenAddModal(false);
  };

  return (
    <div style={{ height: "100%", width: "100%" }}>
      <div style={{ display: "flex", gap: "10px", marginBottom: "10px" }}>
        <CustomButton
          onClick={handleOpenAddModal}
          text="Add Admin Daerah"
          isFullWidth={false}
          baseColor="#003158"
          hoverColor="#00213A"
        />
      </div>
      <CustomDataGrid
        rows={rows}
        loading={loading}
        columns={[
          { field: "id", headerName: "No.", width: 100 },
          { field: "name", headerName: "Nama Admin", flex: 1 },
          {
            field: "daerah",
            headerName: "Daerah",
            flex: 3,
            renderCell: (params) => (
              <div
                style={{
                  whiteSpace: "normal",
                  wordWrap: "break-word",
                  lineHeight: "1.4",
                  textAlign: "left",
                  width: "100%",
                }}
              >
                {Array.isArray(params.value) ? params.value.join(", ") : "-"}
              </div>
            ),
          },
          {
            field: "actions",
            headerName: "Action",
            renderCell: (params) => (
              <RowActionsMenu
                actions={[
                  {
                    label: "Edit",
                    onClick: () => handleEditAdminDaerah(params.row.uuid),
                  },
                  {
                    label: "Hapus",
                    onClick: () => handleDeleteAdminDaerah(params.row.uuid, params.row.name),
                    danger: true,
                  },
                ]}
              />
            ),
            flex: 1,
          },
        ]}
        setPage={setPage}
      />
      <Dialog
        open={openAddModal}
        onClose={handleCloseAddModal}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {isEditMode ? "Edit Admin Daerah" : "Tambah Admin Daerah"}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            {!isEditMode && (
              <Grid item xs={12}>
                <Typography>
                  Nama Anggota<span style={{ color: "red" }}>*</span>
                </Typography>
                <CustomAutocompleteField
                  placeholder="Pilih Anggota"
                  searchBy="userId"
                  displayField="{namaAnggota} - ({emailAnggota})"
                  value={listAnggota.userId}
                  options={listAnggotaItems}
                  onChange={(newValue) => {
                    setListAnggota(newValue);
                  }}
                />
              </Grid>
            )}
            {isEditMode && listAnggota && (
              <Grid item xs={12}>
                <Typography>Nama Anggota</Typography>
                <Typography sx={{ fontWeight: "bold", mt: 1 }}>
                  {listAnggota.namaAnggota}
                  {listAnggota.emailAnggota &&
                    listAnggota.emailAnggota !== "-" && (
                      <> - ({listAnggota.emailAnggota})</>
                    )}
                </Typography>
              </Grid>
            )}
            <Grid item xs={12}>
              <Typography>
                Daerah<span style={{ color: "red" }}>*</span>
              </Typography>
              <CustomMultiAutocompleteField
                placeholder="Pilih Daerah"
                searchBy="id"
                displayField="name"
                withCost={false}
                value={listDaerah}
                options={listDaerahItems}
                onChange={(newValue) => {
                  setListDaerah(newValue);
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <CustomButton
                onClick={handleUpsertAdminDaerah}
                text={isEditMode ? "Update" : "Tambah"}
                isFullWidth={false}
                baseColor="#003158"
                hoverColor="#00213A"
              />
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>
    </div>
  );
}
